'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useMergedRefs } from '@base-ui/utils/useMergedRefs';
import { createSelectorMemoized, useStore } from '@base-ui/utils/store';
import { useResizeObserver } from '@mui/x-internals/useResizeObserver';
import { getDayList } from '@mui/x-scheduler-headless/get-day-list';
import { useAdapterContext } from '@mui/x-scheduler-headless/use-adapter-context';
import { useEventCalendarView } from '@mui/x-scheduler-headless/use-event-calendar-view';
import { useEventCalendarStoreContext } from '@mui/x-scheduler-headless/use-event-calendar-store-context';
import { useExtractEventCalendarParameters } from '@mui/x-scheduler-headless/use-event-calendar';
import { eventCalendarPreferenceSelectors } from '@mui/x-scheduler-headless/event-calendar-selectors';
import { CalendarGrid } from '@mui/x-scheduler-headless/calendar-grid';
import { useEventOccurrencesGroupedByDay } from '@mui/x-scheduler-headless/use-event-occurrences-grouped-by-day';
import { schedulerOtherSelectors } from '@mui/x-scheduler-headless/scheduler-selectors';
import clsx from 'clsx';
import { EventCalendarProvider } from "../internals/components/EventCalendarProvider.mjs";
import MonthViewWeekRow from "./month-view-row/MonthViewWeekRow.mjs";
import { MoreEventsPopoverProvider } from "../internals/components/more-events-popover/index.mjs";
import { useEventCalendarStyledContext } from "../event-calendar/EventCalendarStyledContext.mjs";
import { EventDialogProvider } from "../internals/components/event-dialog/index.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const FIXED_CELL_WIDTH = 28;
const MonthViewRoot = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'MonthView'
})(({
  theme
}) => ({
  width: '100%',
  height: '100%',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${(theme.vars || theme).palette.divider}`,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '100%',
  overflowY: 'auto'
}));
const MonthViewGrid = styled(CalendarGrid.Root, {
  name: 'MuiEventCalendar',
  slot: 'MonthViewGrid'
})({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0
});
const MonthViewHeader = styled(CalendarGrid.HeaderRow, {
  name: 'MuiEventCalendar',
  slot: 'MonthViewHeader'
})(({
  theme,
  ownerState
}) => ({
  display: 'grid',
  gridTemplateColumns: ownerState.showWeekNumber ? `${FIXED_CELL_WIDTH}px repeat(auto-fit, minmax(0, 1fr))` : 'repeat(auto-fit, minmax(0, 1fr))',
  borderBlockEnd: `1px solid ${(theme.vars || theme).palette.divider}`
}));
const MonthViewHeaderCell = styled(CalendarGrid.HeaderCell, {
  name: 'MuiEventCalendar',
  slot: 'MonthViewHeaderCell'
})(({
  theme
}) => ({
  padding: theme.spacing(1),
  textAlign: 'center',
  fontSize: theme.typography.body2.fontSize,
  lineHeight: '18px',
  '&:not(:first-of-type)': {
    borderInlineStart: `1px solid ${(theme.vars || theme).palette.divider}`
  }
}));
const MonthViewWeekHeaderCell = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'MonthViewWeekHeaderCell'
})(({
  theme
}) => ({
  padding: theme.spacing(1, 0),
  textAlign: 'center',
  fontSize: theme.typography.caption.fontSize,
  lineHeight: '18px',
  color: (theme.vars || theme).palette.text.secondary,
  fontStyle: 'italic'
}));
const MonthViewBody = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'MonthViewBody'
})({
  flex: 1,
  display: 'grid',
  gridAutoRows: 'minmax(0, 1fr)',
  position: 'relative',
  flexGrow: 1
});
const CELL_PADDING = 5; // theme.spacing(0.5) * 2
const DAY_NUMBER_HEADER_HEIGHT = 22; // event height (18px) + gap (4px)
const EVENT_HEIGHT = 18;
const EVENT_GAP = 4; // theme.spacing(0.5) = 4px

const MONTH_VIEW_CONFIG = {
  siblingVisibleDateGetter: ({
    state,
    delta
  }) => state.adapter.addMonths(state.adapter.startOfMonth(schedulerOtherSelectors.visibleDate(state)), delta),
  visibleDaysSelector: createSelectorMemoized(state => state.adapter, schedulerOtherSelectors.visibleDate, eventCalendarPreferenceSelectors.showWeekends, (adapter, visibleDate, showWeekends) => getDayList({
    adapter,
    start: adapter.startOfWeek(adapter.startOfMonth(visibleDate)),
    end: adapter.endOfWeek(adapter.endOfMonth(visibleDate)),
    excludeWeekends: !showWeekends
  }))
};

/**
 * A Month View to use inside the Event Calendar.
 */
export const MonthView = /*#__PURE__*/React.memo(/*#__PURE__*/React.forwardRef(function MonthView(props, forwardedRef) {
  // Context hooks
  const adapter = useAdapterContext();
  const {
    classes,
    localeText
  } = useEventCalendarStyledContext();
  const store = useEventCalendarStoreContext();

  // Ref hooks
  const containerRef = React.useRef(null);
  const handleRef = useMergedRefs(forwardedRef, containerRef);
  const cellRef = React.useRef(null);

  // Selector hooks
  const showWeekNumber = useStore(store, eventCalendarPreferenceSelectors.showWeekNumber);

  // State hooks
  const [maxEvents, setMaxEvents] = React.useState(2);

  // Feature hooks
  const {
    days
  } = useEventCalendarView(MONTH_VIEW_CONFIG);
  const weeks = React.useMemo(() => {
    const tempWeeks = [];
    let weekNumber = null;
    for (const day of days) {
      const prevWeek = tempWeeks[tempWeeks.length - 1];
      const dayWeekNumber = adapter.getWeekNumber(day.value);
      if (weekNumber !== dayWeekNumber) {
        weekNumber = dayWeekNumber;
        tempWeeks.push([day]);
      } else {
        prevWeek.push(day);
      }
    }
    return tempWeeks;
  }, [adapter, days]);
  const occurrencesMap = useEventOccurrencesGroupedByDay({
    days
  });
  useResizeObserver(cellRef, () => {
    const cellHeight = cellRef.current.clientHeight;
    const eventContainerHeight = cellHeight - CELL_PADDING - DAY_NUMBER_HEADER_HEIGHT;
    const maxEventsCount = Math.floor(eventContainerHeight / (EVENT_HEIGHT + EVENT_GAP));
    setMaxEvents(Math.max(1, maxEventsCount));
  }, true);
  return /*#__PURE__*/_jsx(MonthViewRoot, _extends({}, props, {
    ref: handleRef,
    className: clsx(props.className, classes.monthView),
    children: /*#__PURE__*/_jsx(MoreEventsPopoverProvider, {
      children: /*#__PURE__*/_jsxs(MonthViewGrid, {
        className: classes.monthViewGrid,
        children: [/*#__PURE__*/_jsxs(MonthViewHeader, {
          className: classes.monthViewHeader,
          ownerState: {
            showWeekNumber
          },
          children: [showWeekNumber && /*#__PURE__*/_jsx(MonthViewWeekHeaderCell, {
            className: classes.monthViewWeekHeaderCell,
            children: localeText.weekAbbreviation
          }), weeks[0].map(weekDay => /*#__PURE__*/_jsx(MonthViewHeaderCell, {
            className: classes.monthViewHeaderCell,
            date: weekDay,
            skipDataCurrent: true,
            children: adapter.formatByString(weekDay.value, 'ccc')
          }, weekDay.key))]
        }), /*#__PURE__*/_jsx(MonthViewBody, {
          className: classes.monthViewBody,
          children: weeks.map((week, weekIdx) => /*#__PURE__*/_jsx(MonthViewWeekRow, {
            maxEvents: maxEvents,
            days: week,
            occurrencesMap: occurrencesMap,
            firstDayRef: weekIdx === 0 ? cellRef : undefined
          }, weekIdx))
        })]
      })
    })
  }));
}));

/**
 * A Month View that can be used outside of the Event Calendar.
 */
if (process.env.NODE_ENV !== "production") MonthView.displayName = "MonthView";
export const StandaloneMonthView = /*#__PURE__*/React.forwardRef(function StandaloneMonthView(props, forwardedRef) {
  const {
    parameters,
    forwardedProps
  } = useExtractEventCalendarParameters(props);
  return /*#__PURE__*/_jsx(EventCalendarProvider, _extends({}, parameters, {
    children: /*#__PURE__*/_jsx(EventDialogProvider, {
      children: /*#__PURE__*/_jsx(MonthView, _extends({
        ref: forwardedRef
      }, forwardedProps))
    })
  }));
});
if (process.env.NODE_ENV !== "production") StandaloneMonthView.displayName = "StandaloneMonthView";